// Reads the gateway URL through the shared normalizer rather than api-client's
// `gatewayBaseUrl`: that barrel eagerly evaluates the SDK client and libs/auth's OIDC config,
// which this gate is a dependency of. Importing it here would be a load-order cycle.
import { getEnv, normalizeBaseUrl } from "@istari-frontend/shared";

const LEGACY_VARS = [
  "VITE_IDENTITY_ROUTER_ENABLED",
  "VITE_IDENTITY_ROUTER_AUTHORITY",
  "VITE_IDENTITY_ROUTER_CLIENT_ID",
];
const NEW_VARS = [
  "VITE_ISTARI_DIGITAL_IDENTITY_SERVICE_ENABLED",
  "VITE_ISTARI_DIGITAL_API_URL",
  "VITE_IDENTITY_SERVICE_CLIENT_ID",
];

// Mirrors libs/auth/src/oidc-config.ts's own copy. Derived independently because libs/auth
// cannot import from src/, so each module tracks its own once-per-load warning.
let warned = false;

function warnIfDeprecatedVarsInUse() {
  if (warned) return;
  const anyLegacy = LEGACY_VARS.some((name) => getEnv(name) !== undefined);
  if (!anyLegacy) return;
  warned = true;
  const anyNew = NEW_VARS.some((name) => getEnv(name) !== undefined);
  console.info(
    anyNew
      ? "VITE_ISTARI_DIGITAL_IDENTITY_SERVICE_ENABLED / VITE_ISTARI_DIGITAL_API_URL / VITE_IDENTITY_SERVICE_CLIENT_ID take precedence over the deprecated VITE_IDENTITY_ROUTER_* variables, which are ignored."
      : "VITE_IDENTITY_ROUTER_* environment variables are deprecated and in use; migrate to VITE_ISTARI_DIGITAL_IDENTITY_SERVICE_ENABLED, VITE_ISTARI_DIGITAL_API_URL, and VITE_IDENTITY_SERVICE_CLIENT_ID.",
  );
}

/**
 * Whether identity-service auth routing is enabled. Reads
 * VITE_ISTARI_DIGITAL_IDENTITY_SERVICE_ENABLED (default false); falls back to the deprecated
 * VITE_IDENTITY_ROUTER_ENABLED only when the new var is unset. Decoupled from gateway-URL
 * presence so that "identity disabled, gateway set" — registry through the gateway (see
 * `api/clientSDK.ts`), OIDC direct to Zitadel — is reachable. Throws when enabled via the new
 * var without VITE_ISTARI_DIGITAL_API_URL, surfacing the misconfiguration at config load; the
 * legacy fallback path skips this check since legacy config carries its own explicit authority.
 *
 * App call sites import this helper so tests can `vi.mock("~/utils/identity-service")`.
 * `libs/auth/src/oidc-config.ts` derives the same gate inline from `getEnv` (it is a library
 * that cannot import from `src/`), and is the only place still doing so.
 */
export function isIdentityServiceEnabled(): boolean {
  warnIfDeprecatedVarsInUse();

  const newEnabledRaw = getEnv("VITE_ISTARI_DIGITAL_IDENTITY_SERVICE_ENABLED");
  const usedLegacyEnabled = newEnabledRaw === undefined;
  const enabledRaw = usedLegacyEnabled ? getEnv("VITE_IDENTITY_ROUTER_ENABLED") : newEnabledRaw;
  const enabled = enabledRaw?.toLowerCase() === "true";

  if (enabled && !usedLegacyEnabled && !normalizeBaseUrl(getEnv("VITE_ISTARI_DIGITAL_API_URL"))) {
    throw new Error(
      "VITE_ISTARI_DIGITAL_IDENTITY_SERVICE_ENABLED is true but VITE_ISTARI_DIGITAL_API_URL is unset or empty after trimming; identity-service mode requires the gateway base URL.",
    );
  }
  return enabled;
}
